import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the "Create New Order" entry points (toolbar button,
 * launcher). Mirrors `useCanEditTemplate.ts` — a single component id gates
 * the whole create-order surface (there's no per-field owner variation to
 * split on, same reasoning as the template edit gate).
 */
export const ORDER_CREATE_COMPONENT_ID = 'order-create';

/**
 * Component id for the "Use Template = N" (skip-template, create from a
 * blank skeleton) path — V1's `access_orders/createWithoutTemplate` grant.
 * Gated separately from `ORDER_CREATE_COMPONENT_ID` because most CSR/LC
 * groups should only ever create from an approved template; skipping the
 * template is a narrower, deliberately-granted capability.
 */
export const ORDER_CREATE_WITHOUT_TEMPLATE_COMPONENT_ID = 'order-create-without-template';

/**
 * Whether the current user may open the Create New Order flow at all.
 *
 * Resolved from the admin-configured component permission (permissions-mode
 * cog) crossed with the user's Azure AD groups. Ships **defaultDisabled**:
 * until an admin grants a group access on `order-create`, the create-order
 * entry points stay hidden for everyone (fallback level 1). Uses the lenient
 * context lookups inside `useResolvedAccessLevel`, so in provider-less
 * test/preview trees it returns the fallback; production views take the real
 * value as a prop (defaulting to editable) so isolated renders are
 * unaffected.
 */
export const useCanCreateOrder = (): boolean =>
  useResolvedAccessLevel(ORDER_CREATE_COMPONENT_ID, false, true) >= 2;

/**
 * Whether the current user may create an order WITHOUT picking a template
 * (V1 `createWithoutTemplate` parity). Only meaningful once `useCanCreateOrder`
 * is already true — callers show the "Use Template" toggle when this is true
 * and force it hidden-`'Y'` otherwise.
 */
export const useCanCreateWithoutTemplate = (): boolean =>
  useResolvedAccessLevel(ORDER_CREATE_WITHOUT_TEMPLATE_COMPONENT_ID, false, true) >= 2;
